import { Client } from "@xmpp/client"
import { useEffect, useState } from "react"
import AdHocCommands from "@/api/adHocCommands"
import { extractVhostFromJid } from "@/jidUtils"
import { Autohof } from "@/lib/autohofs"
import AutohofsParser from "@/lib/autohofsParser"

const autohofsCache = new Map<string, Autohof[]>()
const autohofsRequests = new Map<string, Promise<Autohof[]>>()

const getAutohofsForClient = async (client: Client): Promise<Autohof[]> => {
  const vhost = extractVhostFromJid(client.jid!.toString())
  const cachedAutohofs = autohofsCache.get(vhost)
  if (cachedAutohofs) {
    return cachedAutohofs
  }

  const pendingRequest = autohofsRequests.get(vhost)
  if (pendingRequest) {
    return pendingRequest
  }

  const request = AdHocCommands.getAutohofs(client)
    .then((response) => {
      const autohofs = AutohofsParser.parse(response)?.autohofs ?? []
      autohofsCache.set(vhost, autohofs)
      return autohofs
    })
    .catch((error) => {
      autohofsRequests.delete(vhost)
      throw error
    })

  autohofsRequests.set(vhost, request)
  return request
}

export const useAutohofs = (enabled: boolean): Autohof[] => {
  const [autohofs, setAutohofs] = useState<Autohof[]>(() => {
    if (!enabled || !window.XMPP?.jid) {
      return []
    }

    return (
      autohofsCache.get(extractVhostFromJid(window.XMPP.jid.toString())) ?? []
    )
  })

  useEffect(() => {
    if (!enabled || !window.XMPP?.jid) {
      return
    }

    let isCancelled = false

    void getAutohofsForClient(window.XMPP)
      .then((nextAutohofs) => {
        if (!isCancelled) {
          setAutohofs(nextAutohofs)
        }
      })
      .catch((error) => {
        console.error("Failed to fetch autohofs:", error)
      })

    return () => {
      isCancelled = true
    }
  }, [enabled])

  return autohofs
}
